// Created By Cursor
import React, { useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  alpha,
} from "@mui/material";
import {
  Language as WebsiteIcon,
  Add as AddIcon,
  ArrowBack as BackIcon,
  CheckCircle as CheckIcon,
  School as SchoolIcon,
  Person as PersonIcon,
  Dns as DomainIcon,
  AutoAwesome as SeedIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import PageContentLayout from "../components/layout/PageContentLayout";
import { crmColors } from "../theme/colors";
import websiteApiService from "../services/websiteApiService";

const BRAND = crmColors.primary.main;
const ACCENT = crmColors.energy.main;

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    bgcolor: "#fff",
    "&.Mui-focused": { boxShadow: `0 0 0 3px ${alpha(ACCENT, 0.18)}` },
  },
  "& .MuiInputLabel-root": { fontWeight: 600, fontSize: "0.82rem" },
};

const SEED_ITEMS = [
  "School master & details",
  "About / Mission / Vision",
  "Website admin login",
  "tm_resource (host/uploads)",
  "Summary stats placeholder",
  "About value points",
  "8 mandatory disclosure documents",
  "Current academic session",
  "Principal message (if name given)",
];

const emptyForm = () => ({
  school_name: "",
  school_code: "",
  eduok_school_code: "",
  school_group: "HIGHSCHOOL",
  domain_name: "",
  school_address: "",
  country: "India",
  state: "",
  admin_name: "",
  admin_phone: "",
  admin_email: "",
  admin_id: "ADM01",
  admin_password: "adm@123",
  status: "Active",
  school_tag_line: "Providing quality education and nurturing future-ready learners.",
  school_heading: "Inspiring minds, shaping futures.",
  school_estb_year: String(new Date().getFullYear()),
  affiliated_to: "",
  principal_name: "",
  website: "",
  official_contact_no1: "",
  official_email: "",
});

const Field = (props) => <TextField fullWidth size="small" sx={fieldSx} {...props} />;

const GroupCard = ({ title, subtitle, icon, color = BRAND, children }) => (
  <Paper
    elevation={0}
    sx={{
      borderRadius: 3,
      border: `1px solid ${alpha(color, 0.14)}`,
      overflow: "hidden",
      boxShadow: `0 10px 24px ${alpha(color, 0.06)}`,
      bgcolor: "#fff",
    }}
  >
    <Box
      sx={{
        px: 2.25,
        py: 1.5,
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        borderBottom: `1px solid ${alpha(color, 0.1)}`,
        background: `linear-gradient(135deg, ${alpha(color, 0.1)} 0%, ${alpha(color, 0.02)} 60%, #fff 100%)`,
      }}
    >
      <Avatar sx={{ width: 36, height: 36, bgcolor: color, color: "#fff" }}>{icon}</Avatar>
      <Box>
        <Typography sx={{ fontWeight: 800, fontSize: "0.95rem" }}>{title}</Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Box>
    </Box>
    <Box sx={{ p: 2.25 }}>{children}</Box>
  </Paper>
);

const ManageWebsiteCreate = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const setField = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const canSubmit = useMemo(
    () => form.school_name.trim().length >= 2 && form.school_code.trim().length >= 2,
    [form.school_name, form.school_code]
  );

  const handleCreate = async () => {
    if (!form.school_name.trim()) {
      toast.error("School name is required");
      return;
    }
    if (!form.school_code.trim()) {
      toast.error("School code is required");
      return;
    }
    if (!canSubmit) {
      toast.error("Please fill required fields");
      return;
    }
    setSaving(true);
    const payload = { ...form };
    if (!payload.official_contact_no1) payload.official_contact_no1 = payload.admin_phone;
    if (!payload.official_email) payload.official_email = payload.admin_email;
    if (!payload.website && payload.domain_name) {
      payload.website = `https://${payload.domain_name.replace(/^https?:\/\//i, "")}`;
    }

    const result = await websiteApiService.createWebsiteSchool(payload);
    setSaving(false);

    if (result.success) {
      const code =
        result.data?.seeded?.school_code ||
        result.data?.school?.school_code ||
        form.school_code;
      toast.success(`Website created${code ? ` · ${code}` : ""}`);
      if (code) {
        navigate(`/manage-website/${encodeURIComponent(code)}`);
      } else {
        navigate("/manage-website");
      }
    } else {
      toast.error(result.error || "Failed to create website");
    }
  };

  return (
    <PageContentLayout
      title="Create Website"
      subtitle="Basic school setup · auto-seeds site content"
      darkBar
      barSx={{ minHeight: 68, py: 1.75, px: 2.75 }}
      contentPadding={2}
      breadcrumbs={[
        { label: "Manage Website", icon: <WebsiteIcon sx={{ color: "#fff", fontSize: 18 }} /> },
        { label: "Create Website" },
      ]}
      backTo="/manage-website"
      actions={
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<BackIcon />}
            onClick={() => navigate("/manage-website")}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              color: "#fff",
              borderColor: alpha("#fff", 0.45),
              borderRadius: "10px",
              "&:hover": { borderColor: "#fff", bgcolor: alpha("#fff", 0.1) },
            }}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            disableElevation
            startIcon={saving ? <CircularProgress size={14} sx={{ color: BRAND }} /> : <AddIcon />}
            onClick={handleCreate}
            disabled={saving || !canSubmit}
            sx={{
              textTransform: "none",
              fontWeight: 800,
              bgcolor: "#fff",
              color: BRAND,
              borderRadius: "10px",
              px: 1.75,
              "&:hover": { bgcolor: alpha("#fff", 0.92) },
            }}
          >
            Create & seed
          </Button>
        </Stack>
      }
    >
      <Grid container spacing={2.25}>
        <Grid item xs={12} lg={8}>
          <Stack spacing={2.25}>
            <GroupCard
              title="School identity"
              subtitle="Required basics for tm_school_master"
              icon={<SchoolIcon sx={{ fontSize: 20 }} />}
            >
              <Grid container spacing={1.5}>
                <Grid item xs={12}>
                  <Field
                    label="School name *"
                    value={form.school_name}
                    onChange={setField("school_name")}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Field
                    label="School code *"
                    value={form.school_code}
                    onChange={setField("school_code")}
                    required
                    placeholder="e.g. 1201"
                    helperText="Unique website school code"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Field
                    label="EduOK school code"
                    value={form.eduok_school_code}
                    onChange={setField("eduok_school_code")}
                    placeholder="Optional"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel sx={{ fontWeight: 600 }}>School group</InputLabel>
                    <Select
                      label="School group"
                      value={form.school_group}
                      onChange={setField("school_group")}
                      sx={{ borderRadius: "12px" }}
                    >
                      <MenuItem value="HIGHSCHOOL">High School</MenuItem>
                      <MenuItem value="PRIMARY">Primary</MenuItem>
                      <MenuItem value="PLAYSCHOOL">Play School</MenuItem>
                      <MenuItem value="COLLEGE">College</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Field
                    label="Address"
                    value={form.school_address}
                    onChange={setField("school_address")}
                    multiline
                    minRows={2}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Field label="Country" value={form.country} onChange={setField("country")} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Field label="State" value={form.state} onChange={setField("state")} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel sx={{ fontWeight: 600 }}>Status</InputLabel>
                    <Select
                      label="Status"
                      value={form.status}
                      onChange={setField("status")}
                      sx={{ borderRadius: "12px" }}
                    >
                      <MenuItem value="Active">Active</MenuItem>
                      <MenuItem value="Inactive">Inactive (draft)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </GroupCard>

            <GroupCard
              title="Domain & branding"
              subtitle="Public site identity"
              icon={<DomainIcon sx={{ fontSize: 20 }} />}
              color="#0d9488"
            >
              <Grid container spacing={1.5}>
                <Grid item xs={12} md={6}>
                  <Field
                    label="Domain name"
                    value={form.domain_name}
                    onChange={setField("domain_name")}
                    placeholder="school.example.com"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Field
                    label="Website URL"
                    value={form.website}
                    onChange={setField("website")}
                    placeholder="https://…"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Field
                    label="Tag line"
                    value={form.school_tag_line}
                    onChange={setField("school_tag_line")}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Field
                    label="Heading"
                    value={form.school_heading}
                    onChange={setField("school_heading")}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Field
                    label="Established year"
                    value={form.school_estb_year}
                    onChange={setField("school_estb_year")}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Field
                    label="Affiliated to"
                    value={form.affiliated_to}
                    onChange={setField("affiliated_to")}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Field
                    label="Principal name"
                    value={form.principal_name}
                    onChange={setField("principal_name")}
                  />
                </Grid>
              </Grid>
            </GroupCard>

            <GroupCard
              title="Website admin login"
              subtitle="Credentials for school website admin panel"
              icon={<PersonIcon sx={{ fontSize: 20 }} />}
              color={crmColors.energy.main}
            >
              <Grid container spacing={1.5}>
                <Grid item xs={12} md={6}>
                  <Field label="Admin name" value={form.admin_name} onChange={setField("admin_name")} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Field label="Admin phone" value={form.admin_phone} onChange={setField("admin_phone")} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Field
                    label="Admin email"
                    value={form.admin_email}
                    onChange={setField("admin_email")}
                    type="email"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Field label="Admin ID" value={form.admin_id} onChange={setField("admin_id")} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Field
                    label="Password"
                    value={form.admin_password}
                    onChange={setField("admin_password")}
                    type="text"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Field
                    label="Official contact"
                    value={form.official_contact_no1}
                    onChange={setField("official_contact_no1")}
                    placeholder="Defaults to admin phone"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Field
                    label="Official email"
                    value={form.official_email}
                    onChange={setField("official_email")}
                    placeholder="Defaults to admin email"
                  />
                </Grid>
              </Grid>
            </GroupCard>

            <Stack direction="row" justifyContent="flex-end" spacing={1}>
              <Button
                variant="outlined"
                onClick={() => navigate("/manage-website")}
                sx={{ textTransform: "none", fontWeight: 700, borderRadius: "11px" }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                disableElevation
                startIcon={saving ? <CircularProgress size={14} /> : <AddIcon />}
                onClick={handleCreate}
                disabled={saving || !canSubmit}
                sx={{
                  textTransform: "none",
                  fontWeight: 800,
                  borderRadius: "11px",
                  px: 2.5,
                  bgcolor: BRAND,
                  boxShadow: `0 8px 18px ${alpha(BRAND, 0.28)}`,
                }}
              >
                Create Website & seed data
              </Button>
            </Stack>
          </Stack>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Stack spacing={2}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                overflow: "hidden",
                border: `1px solid ${alpha(BRAND, 0.16)}`,
                background: `linear-gradient(160deg, ${crmColors.primary.darker} 0%, ${crmColors.hero.start} 50%, ${crmColors.hero.end} 100%)`,
                color: "#fff",
                p: 2.25,
              }}
            >
              <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.25 }}>
                <Avatar sx={{ bgcolor: alpha("#fff", 0.18), color: "#fff" }}>
                  <SeedIcon />
                </Avatar>
                <Box>
                  <Typography sx={{ fontWeight: 800 }}>What gets seeded</Typography>
                  <Typography sx={{ fontSize: "0.78rem", opacity: 0.85 }}>
                    Starter rows so the site is ready to edit
                  </Typography>
                </Box>
              </Stack>
              <Stack spacing={0.85}>
                {SEED_ITEMS.map((item) => (
                  <Stack key={item} direction="row" spacing={1} alignItems="flex-start">
                    <CheckIcon sx={{ fontSize: 16, mt: 0.2, opacity: 0.9 }} />
                    <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, lineHeight: 1.35 }}>
                      {item}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 3,
                border: `1px solid ${alpha("#94a3b8", 0.28)}`,
                bgcolor: "#fff",
              }}
            >
              <Typography sx={{ fontWeight: 800, mb: 1 }}>Tips</Typography>
              <Stack spacing={1}>
                <Chip
                  size="small"
                  label="School code is required and must be unique"
                  sx={{ justifyContent: "flex-start", fontWeight: 600, bgcolor: alpha(BRAND, 0.08) }}
                />
                <Chip
                  size="small"
                  label="Password is stored as bcrypt hash"
                  sx={{ justifyContent: "flex-start", fontWeight: 600, bgcolor: alpha(crmColors.success.main, 0.1) }}
                />
                <Chip
                  size="small"
                  label="You can edit everything after create"
                  sx={{ justifyContent: "flex-start", fontWeight: 600, bgcolor: alpha(crmColors.energy.main, 0.1) }}
                />
              </Stack>
            </Paper>
          </Stack>
        </Grid>
      </Grid>
    </PageContentLayout>
  );
};

export default ManageWebsiteCreate;
